import { useState, useEffect } from 'react';
import { getMavrkPayTransactions } from '../utils/supabase';

export interface MavrkPayTransaction {
  id: string;
  user_address: string;
  transaction_type: 'deposit' | 'pay' | 'request';
  amount: string;
  counterparty_address: string | null;
  tx_hash: string | null;
  block_number: number | null;
  created_at: string;
}

/**
 * Hook to fetch MavrkPay transactions from Supabase
 * Much faster and more reliable than querying the blockchain
 */
export function useMavrkPayTransactions(
  userAddress: `0x${string}` | undefined,
  transactionType?: 'deposit' | 'pay' | 'request',
  limit: number = 5
) {
  const [transactions, setTransactions] = useState<MavrkPayTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!userAddress) {
      setTransactions([]);
      return;
    }

    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getMavrkPayTransactions(userAddress, transactionType, limit);
        setTransactions(data);
      } catch (err) {
        console.error('[useMavrkPayTransactions] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [userAddress, transactionType, limit, refreshTrigger]);

  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return { transactions, loading, error, refetch };
}

